/** Reliable Web Speech API alerts for realtime notifications. */

const SPOKEN_STORAGE_KEY = 'ss-spoken-notification-keys';
const SPOKEN_MAX_KEYS = 300;

let speechUnlocked = false;

export function unlockSpeechSynthesis() {
  if (!('speechSynthesis' in window)) return;
  speechUnlocked = true;
  const synth = window.speechSynthesis;
  const utterance = new SpeechSynthesisUtterance('');
  utterance.volume = 0;
  synth.speak(utterance);
  if (synth.paused) synth.resume();
}

export function shouldSpeakNotification(payload: Record<string, unknown>): boolean {
  const type = String(
    payload.event ?? payload.type ?? payload.v1_event ?? payload.notification_type ?? ''
  )
    .trim()
    .toLowerCase();
  if (!type) return false;
  return (
    type.includes('question') ||
    type.includes('message') ||
    type.includes('like') ||
    type === 'chatrequest' ||
    type === 'newmessage' ||
    type === 'profilelike' ||
    type === 'chatrequestaccepted' ||
    type === 'chatrequestdeclined' ||
    type === 'questionanswered'
  );
}

function readSpokenKeys(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SPOKEN_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((k) => typeof k === 'string'));
  } catch {
    return new Set();
  }
}

function writeSpokenKeys(keys: Set<string>) {
  try {
    const list = Array.from(keys).slice(-SPOKEN_MAX_KEYS);
    sessionStorage.setItem(SPOKEN_STORAGE_KEY, JSON.stringify(list));
  } catch {
    // ignore quota errors
  }
}

const ENGAGEMENT_SPEAK_EVENTS = new Set([
  'profilelike',
  'newquestion',
  'chatrequest',
  'profileaccessrequest',
  'referenceconfirmed',
  'questionanswered',
]);

export function notificationSpeakKey(eventName: string, payload: Record<string, unknown>): string {
  const event = eventName.trim().toLowerCase();
  const message = String(payload.message ?? payload.content ?? '').trim();
  if (message && ENGAGEMENT_SPEAK_EVENTS.has(event)) {
    return `${event}:msg:${message.slice(0, 160)}`;
  }

  const id = payload.id ?? payload.messageId ?? payload.notification_id ?? payload.notificationId;
  if (id != null && String(id) !== '' && String(id) !== '0') {
    return `${event}:${id}`;
  }

  if (event === 'newmessage' || event === 'notification') {
    const convId = payload.conversationUuid ?? payload.conversationId;
    const embedded = payload.data as Record<string, unknown> | undefined;
    const msgId = embedded?.id ?? payload.messageId;
    if (convId && msgId) {
      return `newmessage:${convId}:${msgId}`;
    }
  }

  const sectionKey = String(payload.sectionKey ?? payload.target_key ?? '').trim();
  if (sectionKey && (event === 'profilelike' || event.includes('like'))) {
    const actor = senderDisplayName(payload) || String(payload.recruiterName ?? '').trim();
    return `profilelike:${sectionKey}:${actor.toLowerCase()}`;
  }

  const createdAt = String(payload.createdAt ?? payload.created_at ?? '').trim();
  const sender = senderDisplayName(payload).toLowerCase();
  if (createdAt && sender) {
    return `${event}:${createdAt}:${sender}`;
  }

  if (message) {
    return `${event}:${message.slice(0, 120)}`;
  }

  return `${event}:unknown`;
}

export function markNotificationSpoken(key: string): boolean {
  const keys = readSpokenKeys();
  if (keys.has(key)) return false;
  keys.add(key);
  writeSpokenKeys(keys);
  return true;
}

function senderDisplayName(payload: Record<string, unknown>): string {
  const direct = String(payload.name ?? payload.senderName ?? '').trim();
  if (direct) return direct;

  const data = payload.data as Record<string, unknown> | undefined;
  const sender = data?.sender as Record<string, unknown> | undefined;
  const fromSender = [sender?.firstName, sender?.lastName].filter(Boolean).join(' ').trim();
  if (fromSender) return fromSender;

  return '';
}

/** Short phrase for TTS — never read full chat bodies aloud. */
export function formatSpeakAlert(eventName: string, payload: Record<string, unknown>): string {
  const event = eventName.trim().toLowerCase();
  const type = String(payload.type ?? payload.event ?? event)
    .trim()
    .toLowerCase();
  const sender = senderDisplayName(payload);

  if (event === 'newmessage' || type === 'newmessage') {
    return sender ? `You have a new message from ${sender}.` : 'You have a new message.';
  }

  if (event === 'newquestion' || type === 'newquestion' || type.includes('question')) {
    return sender ? `You have a new question from ${sender}.` : 'You have a new question.';
  }

  if (event === 'chatrequest' || type === 'chatrequest') {
    return sender ? `You have a new chat request from ${sender}.` : 'You have a new chat request.';
  }

  if (event === 'chatrequestaccepted' || type === 'chatrequestaccepted') {
    return sender ? `${sender} accepted your chat request.` : 'Your chat request was accepted.';
  }

  if (event === 'chatrequestdeclined' || type === 'chatrequestdeclined') {
    return sender ? `${sender} declined your chat request.` : 'Your chat request was declined.';
  }

  if (event === 'questionanswered' || type === 'questionanswered') {
    return sender ? `${sender} answered your question.` : 'Your question was answered.';
  }

  if (event === 'profilelike' || type === 'profilelike' || type.includes('like')) {
    return sender
      ? `${sender} liked something on your profile.`
      : 'Someone liked something on your profile.';
  }

  const fallback = String(payload.message ?? payload.content ?? '').trim();
  if (fallback.length > 120) {
    return 'You have a new notification.';
  }
  return fallback || 'You have a new notification.';
}

export function speakNotificationMessage(message: string) {
  const text = message.trim();
  if (!text || !('speechSynthesis' in window)) return;

  const synth = window.speechSynthesis;

  const runSpeak = () => {
    if (synth.paused) synth.resume();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;

    const voices = synth.getVoices();
    const voice =
      voices.find((v) => v.lang.startsWith('en-US')) || voices.find((v) => v.lang.startsWith('en'));
    if (voice) utterance.voice = voice;

    synth.speak(utterance);
  };

  synth.cancel();

  window.setTimeout(
    () => {
      if (synth.getVoices().length === 0) {
        const onVoices = () => {
          synth.removeEventListener('voiceschanged', onVoices);
          runSpeak();
        };
        synth.addEventListener('voiceschanged', onVoices);
        return;
      }
      runSpeak();
    },
    speechUnlocked ? 80 : 200
  );
}

const recentSpeakAt = new Map<string, number>();
const recentPhraseAt = new Map<string, number>();
const RECENT_SPEAK_MS = 60_000;

export function speakNotificationOnce(eventName: string, payload: Record<string, unknown>) {
  const key = notificationSpeakKey(eventName, payload);
  if (!markNotificationSpoken(key)) return;

  const now = Date.now();
  const lastAt = recentSpeakAt.get(key);
  if (lastAt != null && now - lastAt < RECENT_SPEAK_MS) return;

  const phrase = formatSpeakAlert(eventName, payload);
  if (!phrase) return;

  const phraseKey = phrase.trim().toLowerCase();
  const lastPhraseAt = recentPhraseAt.get(phraseKey);
  if (lastPhraseAt != null && now - lastPhraseAt < RECENT_SPEAK_MS) return;

  recentSpeakAt.set(key, now);
  recentPhraseAt.set(phraseKey, now);
  speakNotificationMessage(phrase);
}

/** Call once from app shell after user interaction. */
export function primeSpeechNotifications() {
  unlockSpeechSynthesis();
}
